import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import Autocomplete, { type AutocompleteChangeReason } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Fuse from 'fuse.js';
import debounce from 'lodash/debounce';
import { useHistory } from 'react-router-dom';
import { type ListChildComponentProps, FixedSizeList } from 'react-window';

import ListItemLink from '@fd/ui/List/ListItemLink';
import { SettingsMenuItem } from '@fd/ui/List/SettingsList';

import { ONE_SECOND } from '../../../helpers/timeConstants';
import { customActions } from '../../../services/datadogRUM/customActions';
import { shouldDisplay, usePortalUser } from '../../../viewConfiguration';

export interface MenuItemOption {
  title: string;
  path: string;
  icon?: React.ReactNode;
  permissions?: string[];
  viewId?: SettingsMenuItem['viewId'];
  exemptFromViewConfiguration?: boolean;
}

const fuseOptions = {
  keys: [
    'title',
    'path',
    'searchAlias',
    'propertyName',
    'propertyId',
    'salesChannelName',
    'salesChannelId',
    'salesChannelType',
    'searchByTitlePropertyAndId',
    'searchByPropertyAndIdAndTitle',
    'searchByIdAndTitleAndProperty',
    'searchByChannelNameAndIdAndTitle',
    'searchByIdAndChannelNameAndTitle',
    'searchByChannelNameAndTitleAndId',
    'searchByTitleAndChannelNameAndType',
  ],
  threshold: 0.3,
};

const isMac = typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const HOTKEY_DISPLAY = isMac ? '⌘ + K' : 'Ctrl + K';

const StyledAutocomplete = styled(Autocomplete)(() => ({
  '.MuiInputBase-root': {
    padding: '0px 16px!important',
    height: '40px',
  },
}));

const StyledOptionRow = styled(ListItemLink)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1),
  minHeight: 48,
  listStyle: 'none',
}));

type StyledHotkeyProps = {
  hasInputAdornment?: boolean;
};

const StyledHotkey = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasInputAdornment',
})<StyledHotkeyProps>(({ theme, hasInputAdornment }) => ({
  display: 'flex',
  height: '24px',
  padding: '0px 8px',
  alignItems: 'center',
  gap: '0px',
  borderRadius: '4px',
  border: '1px solid rgba(0, 0, 0, 0.10)',
  background: 'rgba(0, 0, 0, 0.04)',
  marginRight: hasInputAdornment ? theme.spacing(1) : 0,
}));

const OptionContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: theme.spacing(1),
}));

const EndAdornmentContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 16,
  alignItems: 'center',
});

// Virtualized Listbox for Autocomplete
const LISTBOX_PADDING = -8; // px
const OPTION_HEIGHT = 56; // px
function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const VirtualizedListbox = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function VirtualizedListbox(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const height = Math.min(8, itemCount) * OPTION_HEIGHT + LISTBOX_PADDING;

    return (
      <div ref={ref} {...other} style={{ height, overflow: 'hidden' }}>
        <FixedSizeList
          height={height}
          width="100%"
          itemSize={OPTION_HEIGHT}
          itemCount={itemCount}
          itemData={itemData}
          overscanCount={0}
        >
          {renderRow}
        </FixedSizeList>
      </div>
    );
  }
);

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 'min-content',
}));

const getPropertyAndSalesChannelInfo = (item: MenuItemOption | SettingsMenuItem) => {
  // joys of typescript
  let propertyName = '';
  if ('propertyName' in item && item.propertyName) {
    propertyName = item.propertyName as string;
  }
  let propertyId = '';
  if ('propertyId' in item && item.propertyId) {
    propertyId = item.propertyId as string;
  }
  let salesChannelName = '';
  if ('salesChannelName' in item && item.salesChannelName) {
    salesChannelName = item.salesChannelName as string;
  }
  let salesChannelId = '';
  if ('salesChannelId' in item && item.salesChannelId) {
    salesChannelId = item.salesChannelId as string;
  }
  let salesChannelType = '';
  if ('salesChannelType' in item && item.salesChannelType) {
    salesChannelType = item.salesChannelType as string;
  }
  return {
    propertyName,
    propertyId,
    salesChannelName,
    salesChannelId,
    salesChannelType,
  };
};
const SettingsSearchBar: React.FC<{
  menuItems: (MenuItemOption | SettingsMenuItem)[];
  placeholder?: string;
  onClickMenuItem?: () => void;
}> = ({ menuItems, placeholder = '', onClickMenuItem }) => {
  const portalUser = usePortalUser();
  const history = useHistory();
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Apply the central display decision before indexing search results.
  const permittedMenuItems = useMemo(() => {
    return menuItems
      .filter((item) =>
        shouldDisplay(portalUser, item.viewId, {
          legacyVisibilityPermissions: item.permissions,
          exemptFromViewConfiguration: item.exemptFromViewConfiguration,
        })
      )
      .map((item) => {
        const { propertyName, propertyId, salesChannelName, salesChannelId, salesChannelType } =
          getPropertyAndSalesChannelInfo(item);
        return {
          // looks weird but to get fuzzy search working with combined search terms we need to add the search terms in different orders
          ...item,
          searchByTitlePropertyAndId: `${item.title ?? ''} ${propertyName} ${propertyId}`,
          searchByPropertyAndIdAndTitle: `${propertyName} ${propertyId} ${item.title ?? ''}`,
          searchByIdAndTitleAndProperty: `${propertyId} ${item.title ?? ''} ${propertyName} `,
          searchByChannelNameAndIdAndTitle: `${salesChannelName} ${salesChannelId} ${item.title ?? ''}`,
          searchByIdAndChannelNameAndTitle: `${salesChannelId} ${salesChannelName} ${item.title ?? ''}`,
          searchByChannelNameAndTitleAndId: `${salesChannelName} ${item.title ?? ''} ${salesChannelId}`,
          searchByTitleAndChannelNameAndType: `${item.title ?? ''} ${salesChannelName} ${salesChannelType}`,
        };
      });
  }, [menuItems, portalUser]);

  const [inputValue, setInputValue] = useState('');

  const [options, setOptions] = useState<MenuItemOption[]>(permittedMenuItems);
  const fuse = useMemo(() => new Fuse(permittedMenuItems, fuseOptions), [permittedMenuItems]);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (!value) {
        setOptions(permittedMenuItems);
      } else {
        const newOptions = fuse.search(value).map((result) => result.item);
        setOptions(newOptions);
        datadogRum.addAction(customActions.SEARCH_PERFORMED, {
          searchValue: value,
          resultCount: newOptions?.length ?? 0,
        });
      }
    }, ONE_SECOND * 0.75),
    [permittedMenuItems, fuse]
  );

  useEffect(() => {
    debouncedSearch(inputValue);
    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, debouncedSearch]);

  // Hotkey: focus input on Cmd+K or Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        (isMac && e.metaKey && e.key.toLowerCase() === 'k') ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')
      ) {
        e.preventDefault();
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSearchResultClick = (option: MenuItemOption) => {
    datadogRum.addAction(customActions.SEARCH_RESULT_CLICKED, {
      searchValue: inputValue,
      resultCount: options?.length ?? 0,
      result: option.path,
    });
  };

  return (
    <StyledAutocomplete
      fullWidth
      size="medium"
      freeSolo
      autoHighlight
      selectOnFocus={false}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(option: MenuItemOption) => option.title ?? ''}
      ListboxComponent={VirtualizedListbox}
      onChange={(_event, value: MenuItemOption | null, _reason: AutocompleteChangeReason) => {
        if (value && typeof value === 'object' && value.path) {
          history.push(value.path);
          onClickMenuItem?.();
          handleSearchResultClick(value);
        }
      }}
      inputValue={inputValue}
      onInputChange={(_event, value: string) => {
        // enter was triggering an undefined value being set to inputValue
        if (
          !_event ||
          (_event.type === 'keydown' &&
            'key' in _event &&
            (_event as React.KeyboardEvent).key === 'Enter')
        ) {
          return;
        }
        setInputValue(value);
      }}
      renderOption={(props, option: MenuItemOption | SettingsMenuItem) => (
        //@ts-expect-error Typing between react-window and material-ui is not working
        <StyledOptionRow {...props} fdKey={option.title} key={option.path} to={option.path}>
          {option?.icon && (
            <StyledListItemIcon data-fd="search-bar-icon">{option?.icon}</StyledListItemIcon>
          )}
          <OptionContent>
            <Typography variant="body2">{option.title}</Typography>{' '}
            {'propertyName' in option &&
            option.propertyName &&
            'propertyId' in option &&
            option.propertyId ? (
              <Typography variant="caption">
                {option.propertyName} &#x2022; {option.propertyId}
              </Typography>
            ) : null}
            {'salesChannelName' in option &&
            option.salesChannelName &&
            'salesChannelId' in option &&
            option.salesChannelId ? (
              <Typography variant="caption">
                {option.salesChannelName} &#x2022; {option.salesChannelId} &#x2022;{' '}
                {option.salesChannelType}
              </Typography>
            ) : null}
          </OptionContent>
        </StyledOptionRow>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder={placeholder}
          size="small"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon data-fd="search-bar-icon" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <EndAdornmentContainer>
                {!isMobile && (
                  <StyledHotkey hasInputAdornment={!!params.InputProps.endAdornment}>
                    <Typography variant="body2">{HOTKEY_DISPLAY}</Typography>
                  </StyledHotkey>
                )}
                {params.InputProps.endAdornment && <div>{params.InputProps.endAdornment}</div>}
              </EndAdornmentContainer>
            ),
          }}
        />
      )}
    />
  );
};

export default SettingsSearchBar;
